import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  api,
  attemptStore,
  ClueOffer,
  GuessView,
  PinSummary,
  Region,
} from "../api";
import { missLine, phraseClue } from "../brand/phrase";
import { BRAND, warmthLine, warmthOfBand } from "../brand/warmth";
import { AnimatedCost, useDropFlash } from "../components/AnimatedNumber";
import { Confetti, haptic } from "../components/Confetti";
import { Tally } from "../components/Tally";
import { ClueEvent, Toast, ToastData } from "../components/Toast";
import { MapView, Pin } from "../components/MapView";
import { Wordmark } from "../components/Wordmark";

type PendingGuess = { lat: number; lng: number; placeId?: number; name: string };

export function Seek() {
  const { pinId = "" } = useParams();
  const navigate = useNavigate();

  const [pin, setPin] = useState<PinSummary | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [budget, setBudget] = useState(100);
  const [guessesLeft, setGuessesLeft] = useState(20);
  const [guessCost, setGuessCost] = useState(5);
  const [winRadiusKm, setWinRadiusKm] = useState(25);
  const [candidates, setCandidates] = useState<number | null>(null);
  const [clues, setClues] = useState<ClueOffer[]>([]);
  const [guesses, setGuesses] = useState<GuessView[]>([]);
  const [pending, setPending] = useState<PendingGuess | null>(null);
  const [dropMode, setDropMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [resigning, setResigning] = useState(false);
  const [region, setRegion] = useState<Region | null>(null);
  const [won, setWon] = useState(false);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [clueEvent, setClueEvent] = useState<
    { id: number; icon: string; text: string; index: number } | null
  >(null);
  /** The place, revealed only once the round is over, so the map can fly to it. */
  const [revealPin, setRevealPin] = useState<Pin | null>(null);
  const toastSeq = useRef(0);
  // The clue list lives in a drawer so the map can own the whole screen.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const started = useRef(false);

  const flash = useDropFlash(candidates ?? 0);

  const refreshRegion = useCallback(async (id: string) => {
    try {
      setRegion(await api.region(id));
    } catch {
      // Shading is a bonus; a failure here must not interrupt the round.
    }
  }, []);


  // ---- start or resume the round ---------------------------------------
  useEffect(() => {
    if (started.current || !pinId) return;
    started.current = true;

    (async () => {
      try {
        const summary = await api.getPin(pinId);
        setPin(summary);
      } catch {
        setFatal("That pin does not exist.");
        return;
      }

      // Resume if this browser already has an attempt on this pin, so a
      // refresh neither loses the round nor hands out a fresh 100 points.
      const existing = attemptStore.get(pinId);
      if (existing) {
        try {
          const state = await api.getAttempt(existing);
          if (state.status !== "active") {
            navigate(`/p/${pinId}/result`, { replace: true });
            return;
          }
          setAttemptId(state.attemptId);
          setBudget(state.budgetRemaining);
          setGuessesLeft(state.guessesRemaining);
          setGuessCost(state.guessCost);
          setWinRadiusKm(state.winRadiusKm);
          setCandidates(state.candidateCount);
          setClues(state.clues);
          setGuesses(state.guesses);
          void refreshRegion(state.attemptId);
          return;
        } catch {
          // The attempt is gone (wiped dev database, pruned row). Start over.
          attemptStore.clear(pinId);
        }
      }

      try {
        const start = await api.startAttempt(pinId);
        attemptStore.set(pinId, start.attemptId);
        setAttemptId(start.attemptId);
        setBudget(start.budgetRemaining);
        setGuessesLeft(start.guessesRemaining);
        setGuessCost(start.guessCost);
        setWinRadiusKm(start.winRadiusKm);
        setCandidates(start.candidateCount);
        setClues(start.clues);
        void refreshRegion(start.attemptId);
      } catch (err) {
        setFatal(
          err instanceof ApiError && err.code === "rate_limited"
            ? "Too many rounds started from here in the last hour. Try again shortly."
            : "Could not start a round.",
        );
      }
    })();
  }, [pinId, navigate, refreshRegion]);

  const buy = useCallback(
    async (type: string) => {
      if (!attemptId || busy) return;
      setBusy(true);
      setError(null);
      try {
        const bought = await api.buyClue(attemptId, type);
        setBudget(bought.budgetRemaining);
        setGuessesLeft(bought.guessesRemaining);
        setCandidates(bought.candidateCount);
        setClues(bought.clues);
        haptic(10);
        setClueEvent({
          id: ++toastSeq.current,
          icon: bought.clues.find((c) => c.type === type)?.icon ?? "🔎",
          text: phraseClue(type, bought.answer, hiderFirstName),
          index: bought.clues.filter((c) => c.bought).length,
        });
        void refreshRegion(attemptId);
      } catch (err) {
        setError(messageFor(err));
      } finally {
        setBusy(false);
      }
    },
    [attemptId, busy, refreshRegion],
  );

  const commitGuess = useCallback(async () => {
    if (!attemptId || !pending || busy) return;
    setBusy(true);
    setError(null);
    const guessName = pending.name;
    try {
      const outcome = await api.guess(
        attemptId,
        pending.lat,
        pending.lng,
        pending.placeId,
      );
      setGuesses(outcome.guesses);
      setGuessesLeft(outcome.guessesRemaining);
      setCandidates(outcome.candidateCount);
      setClues(outcome.clues);
      setBudget(outcome.budgetRemaining);
      setPending(null);
      setDropMode(false);
      void refreshRegion(attemptId);

      const warmth = warmthOfBand(outcome.band);
      setToast({
        id: ++toastSeq.current,
        icon: outcome.correct ? "🎯" : trendIcon(outcome.trend),
        label: outcome.correct
          ? `Found ${hiderFirstName ?? "them"}`
          : `${guessName} · ${trendWord(outcome.trend)}`,
        value: outcome.correct ? "Dead on" : outcome.label,
        sub: outcome.correct
          ? undefined
          : outcome.band >= 4
            ? missLine(outcome.band, hiderFirstName)
            : warmthLine(outcome.band),
        tint: outcome.correct ? BRAND.moss : warmth.color,
      });

      if (outcome.correct) {
        setWon(true);
        haptic([18, 60, 18, 60, 40]);
      } else {
        haptic(outcome.result ? [30, 80, 30] : 14);
      }

      // The round is over. Fly the map to where they actually were before
      // handing over to the result screen — the reveal is the payoff, and it
      // should happen on the board you have been staring at.
      if (outcome.result) {
        const p = outcome.result.place;
        setRevealPin({
          lat: p.lat,
          lng: p.lng,
          label: p.name,
          kind: "target",
        });
        setTimeout(() => navigate(`/p/${pinId}/result`), 2600);
      }
    } catch (err) {
      setError(messageFor(err));
      setPending(null);
    } finally {
      setBusy(false);
    }
  }, [attemptId, pending, busy, navigate, pinId, refreshRegion]);

  const doResign = useCallback(async () => {
    if (!attemptId) return;
    setBusy(true);
    try {
      await api.resign(attemptId);
      navigate(`/p/${pinId}/result`);
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  }, [attemptId, navigate, pinId]);

  const mapPins: Pin[] = useMemo(
    () =>
      guesses.map((g) => ({
        lat: g.lat,
        lng: g.lng,
        label: g.label,
        kind: "guess" as const,
        color: warmthOfBand(g.band).color,
      })),
    [guesses],
  );

  const hiderFirstName = pin?.hiderName?.split(" ")[0] ?? null;

  const closest = useMemo(
    () => (guesses.length ? Math.min(...guesses.map((g) => g.band)) : null),
    [guesses],
  );

  // Every clue purchase has to leave the price of one guess behind, so a round
  // always ends on a guess rather than in a dead position.
  const spendable = budget - guessCost;
  const cheapest = useMemo(
    () =>
      clues
        .filter((c) => !c.bought && !c.locked && c.cost !== null)
        .reduce((min, c) => Math.min(min, c.cost!), Infinity),
    [clues],
  );
  const brokeForEverything = cheapest !== Infinity && cheapest > spendable;

  if (fatal) {
    return (
      <div className="page">
        <h1>{fatal}</h1>
        <p style={{ color: "var(--fog)" }}>
          Check the link, or <Link to="/">hide a spot of your own</Link>.
        </p>
      </div>
    );
  }

  const isNote = (t: string) => t.startsWith("note");
  const notes = clues.filter((c) => isNote(c.type));
  const bought = clues.filter((c) => c.bought && !isNote(c.type));
  const available = clues.filter((c) => !c.bought && !isNote(c.type));

  return (
    <div className="seek">
      {/* The map is the game. Everything else floats on top of it. */}
      <MapView
        pins={revealPin ? [...mapPins, revealPin] : mapPins}
        fitTo={revealPin ? [revealPin] : null}
        fitBox={pin?.arena.bounds ?? null}
        region={revealPin ? null : region}
        dropMode={dropMode}
        fullBleed
        layerToggle
        onDrop={(lat, lng) => {
          if (!dropMode || guessesLeft === 0) return;
          setDropMode(false);
          setPending({ lat, lng, name: `${lat.toFixed(3)}, ${lng.toFixed(3)}` });
        }}
      />

      <div className="hud-top">
        {/* The board, first thing and free. Knowing the hider is only worth
            something once you know where they could be. */}
        <div className="hud-pill">
          <Wordmark size={17} />
          {pin && (
            <span className="hud-arena" title={`${pin.arena.count} possible places`}>
              {pin.arena.label}
            </span>
          )}
        </div>

        <div className="hud-pill hud-stats">
          <span className="hud-stat">
            <span className="hud-stat-label">Budget</span>
            <span className="hud-stat-value budget">
              <Tally value={budget} duration={480} format={(n) => String(n)} />
            </span>
          </span>
          <span className="hud-div" />
          <span className="hud-stat">
            <span className="hud-stat-label">Possible</span>
            <span className={`hud-stat-value candidates ${flash ? "dropping" : ""}`}>
              {candidates === null ? "—" : <Tally value={candidates} />}
            </span>
          </span>
        </div>
      </div>

      {/* Thumb rail. Bottom right on every size, because that is where a hand
          already is on a phone and it costs a desktop user nothing. */}
      <div className="hud-side">
        {closest !== null && (
          <div
            className="hud-warmth"
            style={{ background: warmthOfBand(closest).color }}
          >
            {warmthOfBand(closest).label}
          </div>
        )}

        <button
          className={`fab ${dropMode ? "on" : ""}`}
          onClick={() => setDropMode((m) => !m)}
          disabled={guessesLeft === 0 || busy}
          aria-pressed={dropMode}
          aria-label={dropMode ? "Cancel dropping a pin" : "Drop a pin to guess"}
        >
          <span className="fab-icon">{dropMode ? "✕" : "📍"}</span>
          <span className="fab-text">{dropMode ? "Cancel" : "Guess"}</span>
          {!dropMode && <span className="fab-badge fab-cost">{guessCost}</span>}
        </button>

        <button
          className="fab fab-primary"
          onClick={() => setDrawerOpen(true)}
          aria-label="Ask the hider something"
        >
          <span className="fab-icon">🔎</span>
          <span className="fab-text">Ask</span>
          {available.length > 0 && (
            <span className="fab-badge">{available.length}</span>
          )}
        </button>
      </div>

      {dropMode && (
        <div className="drop-hint">Tap the map where you think it is</div>
      )}

      {/* Clue drawer */}
      <div
        className={`drawer-scrim ${drawerOpen ? "on" : ""}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      <aside
        className={`drawer ${drawerOpen ? "on" : ""}`}
        aria-label="Clues"
        aria-hidden={!drawerOpen}
      >
        <div className="drawer-head">
          <div>
            <div className="label">Ask {hiderFirstName ?? "them"}</div>
            <div className="clue-sub">
              Three questions at a time, somewhere in {pin?.arena.label ?? "the world"}
            </div>
          </div>
          <button
            className="drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="drawer-scroll">
          {notes.length > 0 && (
            <>
              <div className="label" style={{ marginBottom: 8 }}>
                From the hider
              </div>
              {notes.map((note) =>
                note.bought ? (
                  <div className="note note-open" key={note.type}>
                    <span className="clue-icon">💬</span>
                    <div>
                      <div className="clue-sub">{note.label}</div>
                      <div className="note-text">{note.answer}</div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={note.type}
                    className="note note-buy"
                    disabled={busy || (note.cost ?? 0) > spendable}
                    onClick={() => buy(note.type)}
                    aria-label={`Read ${note.label} for ${note.cost} points`}
                  >
                    <span className="clue-icon">💬</span>
                    <span>
                      <span className="clue-label">{note.label}</span>
                      {(note.cost ?? 0) > spendable && (
                        <span className="clue-sub"> · too expensive</span>
                      )}
                    </span>
                    <span className="cost">
                      {note.cost === 0 ? "free" : note.cost}
                    </span>
                  </button>
                ),
              )}
              <p className="clue-sub" style={{ margin: "10px 0 20px" }}>
                Written clues do not narrow the count below — that is on you.
              </p>
            </>
          )}

          {bought.length > 0 && (
            <>
              <div className="label" style={{ marginBottom: 8 }}>
                What they have told you
              </div>
              {bought.map((clue) => (
                <div className="revealed" key={clue.type}>
                  <span className="clue-icon">{clue.icon}</span>
                  <div>
                    <div className="clue-sub">{clue.question}</div>
                    <div className="revealed-answer">
                      {phraseClue(clue.type, clue.answer ?? "", hiderFirstName)}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {available.length > 0 && (
            <div className="label" style={{ margin: "18px 0 8px" }}>
              {bought.length > 0 ? "Ask something else" : "Ask them"}
            </div>
          )}

          {available.map((clue) => {
            const affordable = clue.cost !== null && clue.cost <= spendable;
            const disabled = busy || clue.locked || !affordable;
            return (
              <button
                key={clue.type}
                className="clue clue-question"
                disabled={disabled}
                onClick={() => buy(clue.type)}
                aria-label={`Ask "${clue.question}" for ${clue.cost} points`}
              >
                <span className="clue-icon">{clue.icon}</span>
                <span>
                  <span className="clue-label">{clue.question}</span>
                  {clue.locked && (
                    <span className="clue-sub"> · {clue.lockedReason}</span>
                  )}
                  {!clue.locked && !affordable && (
                    <span className="clue-sub"> · too expensive</span>
                  )}
                </span>
                <AnimatedCost value={clue.cost} affordable={affordable} />
              </button>
            );
          })}

          <p className="clue-sub" style={{ marginTop: 10 }}>
            {brokeForEverything
              ? `Nothing left you can afford to ask. Keep hunting — a guess is ${guessCost}.`
              : "Answering one changes what they will answer next."}
          </p>

          {guesses.length > 0 && (
            <>
              <div className="label" style={{ margin: "20px 0 8px" }}>
                Where you have looked
              </div>
              {guesses.map((g, i) => {
                const warmth = warmthOfBand(g.band);
                return (
                  <div className="guess-log-row" key={i}>
                    <span className="coord" style={{ fontSize: 13 }}>
                      {g.lat.toFixed(2)}, {g.lng.toFixed(2)}
                      {g.trend && (
                        <span className="clue-sub"> · {trendWord(g.trend)}</span>
                      )}
                    </span>
                    <span className="dist num" style={{ color: warmth.color }}>
                      {g.label}
                    </span>
                  </div>
                );
              })}
            </>
          )}

          {error && (
            <p style={{ color: "var(--flare)", fontSize: 14 }} role="alert">
              {error}
            </p>
          )}

          <button
            className="btn btn-ghost"
            style={{ width: "100%", color: "var(--fog)", marginTop: 22 }}
            onClick={() => {
              setDrawerOpen(false);
              setResigning(true);
            }}
            disabled={busy || !attemptId}
          >
            Give up and reveal
          </button>
        </div>
      </aside>

      <Confetti fire={won} />
      <ClueEvent event={clueEvent} onDone={() => setClueEvent(null)} />
      <Toast toast={toast} onDone={() => setToast(null)} ms={won ? 2600 : 4200} />

      {pending && (
        <Confirm
          title={`Look here for ${guessCost}?`}
          body={
            <>
              <p style={{ marginTop: 0 }}>
                Guessing <strong>{pending.name}</strong>.
              </p>
              <p className="clue-sub" style={{ marginBottom: 0 }}>
                Within {winRadiusKm} km wins the round. Otherwise you get a
                temperature and whether it beat your last look — this is one of{" "}
                {guessesLeft} you can still afford.
              </p>
            </>
          }
          confirmLabel="Look"
          onCancel={() => setPending(null)}
          onConfirm={commitGuess}
          busy={busy}
        />
      )}

      {resigning && (
        <Confirm
          title="Give up?"
          body={
            <p style={{ marginTop: 0 }}>
              The spot gets revealed and the round is scored with{" "}
              <span className="num">{budget}</span> points unspent.
            </p>
          }
          confirmLabel="Give up"
          onCancel={() => setResigning(false)}
          onConfirm={doResign}
          busy={busy}
        />
      )}
    </div>
  );
}

function Confirm({
  title,
  body,
  confirmLabel,
  onCancel,
  onConfirm,
  busy,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="scrim" onClick={onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: 10 }}>{title}</h2>
        {body}
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Warmer / colder, the only comparison the seeker is given. */
const trendWord = (trend: GuessView["trend"]) =>
  trend === "warmer" ? "warmer" : trend === "colder" ? "colder" : "no change";

const trendIcon = (trend: GuessView["trend"]) =>
  trend === "warmer" ? "🔥" : trend === "colder" ? "🧊" : "📍";

function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return "Something went wrong.";
  switch (err.code) {
    case "insufficient_budget":
      return "Not enough left — you always keep enough back for one guess.";
    case "already_bought":
      return "They have already answered that one.";
    case "not_offered":
      return "That is not on the table right now.";
    case "locked":
      return "Make a guess first to unlock that clue.";
    case "attempt_over":
      return "This round is already over.";
    case "rate_limited":
      return "Too many requests. Give it a minute.";
    default:
      return "Something went wrong.";
  }
}
